import { useEffect, useRef, useState } from 'react';
import StochasticCanvas from './StochasticCanvas';
import ForecastSimulation from './ForecastSimulation';
import ForecastSimulationMobile from './ForecastSimulationMobile';
// import StipplePortrait from './StipplePortrait';
import teamRaw from './teamData/team.json';
import investorsRaw from './teamData/investors.json';
import './App.css';

/* Resolve image/logo paths relative to teamData subfolders */
const TEAM = teamRaw.map((m) => ({
  ...m,
  image: require(`./teamData/images/${m.image}`),
  logos: m.logos.map((l) => require(`./teamData/logos/${l}`)),
}));

const INVESTORS = investorsRaw.length > 0
  ? investorsRaw.map((inv) => ({
      ...inv,
      logo: require(`./teamData/investorLogos/${inv.logo}`),
    }))
  : [];

function App() {
  const revealRefs = useRef([]);
  const [expandedIdx, setExpandedIdx] = useState(null);
  const showFull = false; // gated — flip to re-enable full page

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    revealRefs.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const addRevealRef = (el) => {
    if (el && !revealRefs.current.includes(el)) {
      revealRefs.current.push(el);
    }
  };

  return (
    <>
      <StochasticCanvas />

      <div className="page">
        {/* ===== HERO ===== */}
        <section className="hero">
          <div className="hero__inner">
            <div className="hero__brand entrance entrance--1">
              <img
                src={`${process.env.PUBLIC_URL}/CONDITIONAL.png`}
                alt="CONDITIONAL"
                className="hero__logo"
              />
              <h1 className="hero__title">CONDITIONAL</h1>
            </div>
          </div>
        </section>

        {showFull && <>
        {/* ===== DIVIDER ===== */}
        <div className="divider reveal" ref={addRevealRef} />

        {/* ===== SIMULATION ===== */}
        <section className="section section--sim reveal" ref={addRevealRef}>
          <h2 className="section__title">Deriving the Future</h2>
          <p className="section__subtitle">
            Producing accurate forecasts on any topic through an amalgamation of
            the most advanced research on artificial intelligence.
          </p>
          <ForecastSimulation />
          <ForecastSimulationMobile />
        </section>

        {/* ===== DIVIDER ===== */}
        <div className="divider reveal" ref={addRevealRef} />

        {/* ===== TEAM ===== */}
        <section className="section section--team reveal" ref={addRevealRef}>
          <h2 className="section__title">The Team</h2>
          <p className="section__subtitle">
            CONDITIONAL is built by forerunners across prediction markets,
            machine learning, and quantitative research. We are actively hiring
            researchers and engineers interested in building universal
            forecasting infrastructure.
          </p>
          <div className="team-grid">
            {TEAM.map((member, i) => (
              <div
                key={i}
                className={`team-card${expandedIdx === i ? ' team-card--active' : ''}`}
                onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}
              >
                <span className="team-card__name">{member.name}</span>
                <span className="team-card__title">{member.title}</span>
                {/* Inline drawer — visible only on mobile */}
                <div className={`team-card__inline-drawer${expandedIdx === i ? ' team-card__inline-drawer--open' : ''}`}>
                  <div className="team-drawer__inner">
                    <div className="team-drawer__img-block">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="team-drawer__photo"
                      />
                      <div className="team-drawer__logos">
                        {member.logos.map((logo, li) => (
                          <img key={li} src={logo} alt="" className="team-drawer__logo" />
                        ))}
                      </div>
                    </div>
                    <p className="team-drawer__desc">{member.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Drawer — always in DOM, slides open when a card is selected */}
          <div className={`team-drawer${expandedIdx !== null ? ' team-drawer--open' : ''}`}>
            {expandedIdx !== null && (() => {
              const member = TEAM[expandedIdx];
              return (
                <div className="team-drawer__inner">
                  <div className="team-drawer__img-block">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="team-drawer__photo"
                    />
                    <div className="team-drawer__logos">
                      {member.logos.map((logo, li) => (
                        <img
                          key={li}
                          src={logo}
                          alt=""
                          className="team-drawer__logo"
                        />
                      ))}
                    </div>
                  </div>
                  <div className="team-drawer__info">
                    <span className="team-drawer__name">{member.name}</span>
                    <span className="team-drawer__title">{member.title}</span>
                    <p className="team-drawer__desc">{member.description}</p>
                  </div>
                </div>
              );
            })()}
          </div>
        </section>

        {/* ===== INVESTORS ===== */}
        {INVESTORS.length > 0 && (
          <>
            <div className="divider reveal" ref={addRevealRef} />
            <section className="section section--investors reveal" ref={addRevealRef}>
              <h2 className="section__title">Backed by the Best</h2>
              <div className="investor-row">
                {INVESTORS.map((inv, i) => (
                  <a
                    key={i}
                    href={inv.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="investor-logo-link"
                  >
                    <img src={inv.logo} alt="" className="investor-logo" />
                  </a>
                ))}
              </div>
            </section>
          </>
        )}

        {/* ===== FOOTER ===== */}
        <footer className="footer">
          <span className="footer__copy">&copy; 2026 CONDITIONAL</span>
        </footer>
        </>}
      </div>
    </>
  );
}

export default App;
